import axios from 'axios';

/**
 * Service for credential capture operations via proxy interception
 */
const captureService = {
  /**
   * Start credential capture for a provider
   * @param {number} providerId - Provider ID
   * @returns {Promise} - Returns connection info (host, port)
   */
  startCapture(providerId) {
    return axios.post(`dashboard/providers/${providerId}/capture/start`);
  },

  /**
   * Get capture status for a provider
   * @param {number} providerId - Provider ID
   * @returns {Promise} - Returns state: idle/waiting/captured and credentials if captured
   */
  getStatus(providerId) {
    return axios.get(`dashboard/providers/${providerId}/capture/status`);
  },

  /**
   * Confirm and save captured credentials
   * @param {number} providerId - Provider ID
   * @returns {Promise} - Saves credentials to provider
   */
  confirmCapture(providerId) {
    return axios.post(`dashboard/providers/${providerId}/capture/confirm`);
  },

  /**
   * Reject captured credentials and continue waiting
   * @param {number} providerId - Provider ID
   * @returns {Promise} - Clears capture, stays in waiting state
   */
  rejectCapture(providerId) {
    return axios.post(`dashboard/providers/${providerId}/capture/reject`);
  },

  /**
   * Stop capture session without saving
   * @param {number} providerId - Provider ID
   * @returns {Promise} - Stops proxy without saving
   */
  stopCapture(providerId) {
    return axios.post(`dashboard/providers/${providerId}/capture/stop`);
  },

  /**
   * Download CA certificate for device setup
   * @returns {Promise} - Returns certificate blob
   */
  downloadCertificate() {
    return axios.get('dashboard/proxy/certificate', {
      responseType: 'blob'
    });
  },

  /**
   * Get intercepted requests list (for request viewing)
   * @param {Object} params - Query params (page, per_page, host, method, status_code)
   * @returns {Promise} - Returns paginated request list
   */
  getRequests(params = {}) {
    // Remove empty params
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
    );
    return axios.get('dashboard/proxy/requests', { params: cleanParams });
  },

  /**
   * Get single request detail
   * @param {number} requestId - Request ID
   * @returns {Promise} - Returns request details
   */
  getRequestDetail(requestId) {
    return axios.get(`dashboard/proxy/requests/${requestId}`);
  },

  /**
   * Clear all intercepted requests
   * @param {string} sessionId - Optional session ID
   * @returns {Promise}
   */
  clearRequests(sessionId = null) {
    const params = sessionId ? { session_id: sessionId } : {};
    return axios.delete('dashboard/proxy/requests', { params });
  },

  /**
   * Get proxy status
   * @returns {Promise} - Returns proxy running state and connection info
   */
  getProxyStatus() {
    return axios.get('dashboard/proxy/status');
  }
};

export default captureService;
